import { useEffect, useState } from 'react';
import { Smartphone, X, Apple, Smartphone as Android, ChevronRight } from 'lucide-react';
import { trackFunnelEvent } from '@/utils/tracking';
import { getDeviceBucket } from '@/utils/deviceBucket';
import { getPlatformBucket, isStandaloneDisplay } from '@/utils/platformBucket';

const DISMISS_KEY = 'ps_admin_pwa_card_dismissed_v1';
const TRACKED_KEY = 'ps_pwa_installed_tracked_v1';

function isStandalone() {
  return isStandaloneDisplay();
}

function trackPwaInstalledOnce(source) {
  // PII-free; one-shot per browser (localStorage guard).
  try {
    if (localStorage.getItem(TRACKED_KEY) === '1') return;
    trackFunnelEvent('pwa_installed', {
      meta: {
        device_type: getDeviceBucket(),
        platform: getPlatformBucket(),
        source, // 'appinstalled_event' | 'standalone_detected' | 'beforeinstallprompt_accepted'
        is_standalone: isStandaloneDisplay(),
      },
    });
    localStorage.setItem(TRACKED_KEY, '1');
  } catch (e) { /* ignore */ }
}

/**
 * AdminPwaInstallCard
 * Admin-only card with Android + iOS install instructions for the Powersure PWA.
 * Hidden when already running in standalone mode or when the user has dismissed
 * the card. No external dependencies.
 */
export default function AdminPwaInstallCard() {
  const [dismissed, setDismissed] = useState(false);
  const [standalone, setStandalone] = useState(false);
  const [installable, setInstallable] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
    } catch (e) { /* ignore */ }
    const standaloneNow = isStandalone();
    setStandalone(standaloneNow);

    // Fire pwa_installed once if we land in standalone mode (covers iOS
    // Safari add-to-home-screen, which has no appinstalled event).
    if (standaloneNow) {
      trackPwaInstalledOnce('standalone_detected');
    }

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setInstallable(true);
    };
    const onAppInstalled = () => {
      // Chrome/Edge/Android fire this after the user accepts the install.
      trackPwaInstalledOnce('appinstalled_event');
    };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  if (standalone || dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* ignore */ }
    setDismissed(true);
  };

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        // Fire as soon as the user accepts; appinstalled will also fire
        // but the guard in trackPwaInstalledOnce dedupes it.
        trackPwaInstalledOnce('beforeinstallprompt_accepted');
      }
    } catch (e) { /* user dismissed */ }
    setDeferredPrompt(null);
    setInstallable(false);
  };

  return (
    <div
      className="rounded-2xl border border-emerald-400/30 p-4 sm:p-5 relative shadow-2xl text-white"
      style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
      data-testid="admin-pwa-install-card"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 p-1 text-white/45 hover:text-white transition-colors"
        aria-label="Sluiten"
        data-testid="admin-pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-5 h-5 text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-medium text-white" style={{ fontFamily: 'Outfit' }}>
            Powersure als app gebruiken
          </h3>
          <p className="text-[11px] sm:text-xs text-white/65 mt-1 leading-relaxed">
            Zet Powersure op het beginscherm van je telefoon. Eén tik en je zit direct in het Control Center — zonder browser-rommel.
          </p>

          {installable && (
            <button
              onClick={triggerInstall}
              className="mt-3 px-3 py-1.5 text-xs bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-md font-semibold inline-flex items-center gap-1.5 transition-colors"
              data-testid="admin-pwa-install-btn"
            >
              <Smartphone className="w-3 h-3" /> Installeer Powersure nu
              <ChevronRight className="w-3 h-3" />
            </button>
          )}

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-pwa-install-instructions">
            <div className="bg-white/5 border border-white/10 rounded-lg p-3" data-testid="admin-pwa-install-android">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-300/90 font-semibold mb-1">
                <Android className="w-3 h-3" /> Android
              </div>
              <ol className="text-[11px] sm:text-xs text-white/80 leading-relaxed list-decimal pl-4 space-y-0.5">
                <li>Open Powersure in Chrome</li>
                <li>Tik op het menu (⋮ rechtsboven)</li>
                <li>Kies &laquo;App installeren&raquo; of &laquo;Toevoegen aan startscherm&raquo;</li>
                <li>Open Powersure via het app-icoon</li>
              </ol>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3" data-testid="admin-pwa-install-ios">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-300/90 font-semibold mb-1">
                <Apple className="w-3 h-3" /> iPhone
              </div>
              <ol className="text-[11px] sm:text-xs text-white/80 leading-relaxed list-decimal pl-4 space-y-0.5">
                <li>Open Powersure in Safari</li>
                <li>Tik op het deel-icoon (□↑)</li>
                <li>Kies &laquo;Zet op beginscherm&raquo;</li>
                <li>Open Powersure via het app-icoon</li>
              </ol>
            </div>
          </div>

          <p className="text-[10px] text-white/45 mt-2 italic">
            Standalone-mode: minder browser-rommel · zelfde URL · zelfde inlog · geen App Store nodig.
          </p>
        </div>
      </div>
    </div>
  );
}
